import { CalendarDays, LogOut, Menu, Settings, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useAuth } from '@/app/providers/AuthProvider';
import { Avatar, Button, Drawer, Icon, Logo, Sidebar, type SidebarSection } from '@/shared/components';
import styles from './AppShell.module.scss';

/** Âncora do conteúdo principal. O skip link aponta para cá. */
const ID_CONTEUDO = 'conteudo-principal';

// Lista plana, sem micro-label de seção: é o desenho do sistema atual, e com
// três destinos o agrupamento só somava ruído.
const SECOES: SidebarSection[] = [
  {
    items: [
      { to: '/agenda', label: 'Agenda', icon: <Icon as={CalendarDays} size={20} /> },
      { to: '/prontuarios', label: 'Prontuários', icon: <Icon as={Users} size={20} /> },
      {
        to: '/configuracoes/usuarios',
        label: 'Usuários',
        icon: <Icon as={Settings} size={20} />,
      },
    ],
  },
];

/**
 * Esqueleto das telas internas.
 *
 * Três coisas moram aqui porque não podem depender de cada tela lembrar delas:
 *
 * 1. **O skip link.** Sem ele, quem navega por teclado percorre a navegação
 *    inteira antes de chegar ao conteúdo, em toda troca de página.
 * 2. **Os landmarks.** A navegação é `<nav>`, o conteúdo é `<main>`. É o que
 *    permite pular direto para uma região com leitor de tela.
 * 3. **A navegação em telas pequenas.** Abaixo de `md` a barra lateral fixa não
 *    cabe: vira um `Drawer`, que já traz foco preso e `Esc`.
 */
export function AppShell() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [menuAberto, setMenuAberto] = useState(false);

  // Navegar fecha o menu: deixá-lo aberto sobre a página nova esconde o conteúdo
  // que o usuário acabou de pedir.
  useEffect(() => {
    setMenuAberto(false);
  }, [location.pathname]);

  const sair = useCallback(() => {
    void signOut();
  }, [signOut]);

  const rodape = (
    <div className={styles.conta}>
      <Avatar name={user?.nome ?? ''} src={user?.foto ?? undefined} size="sm" />
      <div className={styles.contaTexto}>
        <p className={styles.contaNome}>{user?.nome}</p>
        <p className={styles.contaEmail}>{user?.email}</p>
      </div>
      <Button
        size="sm"
        variant="tertiary"
        onClick={sair}
        aria-label="Sair da conta"
        icon={<Icon as={LogOut} size={16} />}
      />
    </div>
  );

  return (
    <div className={styles.shell}>
      {/*
        Primeiro elemento focável da página, visível só quando recebe foco — o
        atalho que evita percorrer a navegação inteira a cada troca de tela.
      */}
      <a href={`#${ID_CONTEUDO}`} className={styles.skip}>
        Pular para o conteúdo
      </a>

      <div className={styles.lateral}>
        <div className={styles.marca}>
          <Logo width={156} className={styles.marcaLogo} />
        </div>
        <Sidebar sections={SECOES} className={styles.nav} />
        {rodape}
      </div>

      <div className={styles.corpo}>
        <div className={styles.barraMobile}>
          <Button
            variant="tertiary"
            onClick={() => {
              setMenuAberto(true);
            }}
            aria-label="Abrir navegação"
            aria-expanded={menuAberto}
            icon={<Icon as={Menu} size={20} />}
          />
          <Logo />
        </div>

        {/*
          `tabIndex={-1}` torna o `<main>` alvo válido do skip link: sem ele o
          foco não se move e o atalho só rola a página.
        */}
        <main id={ID_CONTEUDO} className={styles.principal} tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      <Drawer
        open={menuAberto}
        onClose={() => {
          setMenuAberto(false);
        }}
        title="Navegação"
      >
        <Sidebar sections={SECOES} />
        {rodape}
      </Drawer>
    </div>
  );
}
